import * as i0 from '@angular/core';
import { Injectable, inject, PLATFORM_ID } from '@angular/core';
export * from 'ngx-cookie-service';
export * from 'ngx-device-detector';
import { isArray, isNullOrUndefined } from 'ngx-unificator/helpers';
import { isPlatformBrowser, DOCUMENT } from '@angular/common';
import { ReplaySubject, fromEvent, merge, Observable, timer, of, concat, defer } from 'rxjs';
import { debounceTime, tap, share, map, shareReplay, first } from 'rxjs/operators';
import { WINDOW } from '@ng-web-apis/common';
import { HttpClient } from '@angular/common/http';
import { CONFETTI$ } from 'ngx-unificator/tokens';
class BaseArrayService {
  constructor() {}
  /**
   * Split array to chunks
   *
   * Example:
   *  toChunks([1, 2, 3, 4, 5, 6, 7], 3)
   *
   * Output:
   *  [[1, 2, 3], [4, 5, 6], [7]]
   *
   * @param original
   * @param chunkSize
   */
  toChunks(original, chunkSize) {
    const result = [];
    for (let i = 0; i < original.length; i += chunkSize) {
      result.push(original.slice(i, i + chunkSize));
    }
    return result;
  }
  /**
   * Cycle array items
   *
   * @param {any[]} originalArray
   * @param {number} count
   * @returns {any[]}
   * @private
   */
  cycle(originalArray, count) {
    const newArray = [];
    if (!originalArray || !originalArray.length) {
      return newArray;
    }
    while (newArray.length < count) {
      for (const arr of originalArray) {
        if (newArray.length < count) {
          newArray.push(arr);
        }
      }
    }
    return newArray;
  }
  /**
   * Returns array from object values
   *
   * Example:
   *  fromObjectValues({
   *    error: {
   *      amount: 'not enough funds'
   *    }
   *  })
   *
   * Output:
   *  ['not enough funds']
   *
   * @param object
   */
  fromObjectValues(object) {
    const list = [];
    Object.values(object).forEach(value => typeof value === 'object' ? list.push(...this.fromObjectValues(value)) : list.push(value));
    return list;
  }
  /**
   * Returns array of url segments from string
   * @param route
   * @private
   */
  routeToArray(route) {
    const url = route.split('?')[0];
    return url.split('/').splice(1);
  }
  /**
   * Check if array has min length of game and clone it
   * @param gameList
   * @param slideCount
   * @private
   */
  checkAndCloneArray(gameList, slideCount) {
    let cloneArr = [];
    const arrayToChunks = this.toChunks(gameList, slideCount);
    if (gameList.length % slideCount === 0) {
      cloneArr = gameList;
    } else {
      cloneArr = this.cycle(gameList, gameList.length + slideCount - arrayToChunks[arrayToChunks.length - 1].length);
    }
    return cloneArr;
  }
  /**
   * Shuffle array
   * @param array
   */
  shuffle(array) {
    let currentIndex = array.length;
    let temporaryValue;
    let randomIndex;
    while (0 !== currentIndex) {
      randomIndex = Math.floor(Math.random() * currentIndex);
      currentIndex -= 1;
      temporaryValue = array[currentIndex];
      array[currentIndex] = array[randomIndex];
      array[randomIndex] = temporaryValue;
    }
    return array;
  }
  /**
   * Move element position in array
   * var arr = [ 'a', 'b', 'c', 'd', 'e'];
   * arr.move(3,1);//["a", "d", "b", "c", "e"]
   * @param array
   * @param from
   * @param to
   * @returns array
   */
  move(array, from, to) {
    array.splice(to, 0, array.splice(from, 1)[0]);
    return array;
  }
  /**
   * Returns array of unique object values from array of objects by key
   *
   * Example:
   *  uniqueBy('id', [
   *    {id: 21, amount: 1},
   *    {id: 21, amount: 2},
   *    {id: 4, amount: 5},
   *  ])
   *
   * Output:
   *  [
   *    {id: 21, amount: 1},
   *    {id: 4, amount: 5},
   *  ]
   *
   * @param key
   */
  uniqueBy(prop, arr) {
    return [...new Map(arr.map(item => [item[prop], item])).values()];
  }
  /**
   * Sort array of objects based on another array
   */
  mapOrder(array, order, key) {
    array.sort(function (a, b) {
      const A = a[key],
        B = b[key];
      if (order.indexOf(A) > order.indexOf(B)) {
        return 1;
      } else {
        return -1;
      }
    });
    return array;
  }
  /**
   * Generic function can be with your custom condition to compare two arrays and get their difference array
   * @param oldArray
   * @param newArray
   * @param customCondition
   * @private
   */
  arrayDifference(oldArray, newArray, customCondition = null) {
    return [...oldArray.filter(o1 => !newArray.some(o2 => customCondition ? customCondition(o1, o2) : o1 === o2)), ...newArray.filter(o1 => !oldArray.some(o2 => customCondition ? customCondition(o1, o2) : o1 === o2))];
  }
  /**
   * Example:
   * [26, 27, 26, 26, 28, 28, 29, 29, 30] => [26, 27, 28, 29, 30]
   * @param array
   * @returns
   */
  unique(array) {
    const arr = [];
    if (array?.length) {
      for (let i = 0; i < array.length; i++) {
        if (!arr.includes(array[i])) {
          arr.push(array[i]);
        }
      }
    }
    return arr;
  }
}
let ArrayService = /*#__PURE__*/(() => {
  class ArrayService extends BaseArrayService {
    constructor() {
      super();
    }
    static {
      this.ɵfac = function ArrayService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || ArrayService)();
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: ArrayService,
        factory: ArrayService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return ArrayService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
var array_service = /*#__PURE__*/Object.freeze({
  __proto__: null,
  ArrayService: ArrayService,
  BaseArrayService: BaseArrayService
});
class BaseAudioService {
  constructor() {
    /**
     * Audio instance
     */
    this._audios = new Map();
  }
  /**
   * Load audio
   * @param audioName
   */
  loadAudio(audioName) {
    const audios = isArray(audioName) ? audioName : [audioName];
    audios.forEach(audio => {
      if (!this._audios.has(audio)) {
        this._audios.set(audio, new Audio(this.src + audio));
        this._audios.get(audio).load();
      }
    });
  }
  /**
   * Play audio
   * @param audioName
   */
  play(audioName) {
    if (this._audios.has(audioName)) {
      this._audios.get(audioName).play();
    }
  }
  /**
   * @param audioName
   */
  stop(audioName) {
    if (this._audios.has(audioName)) {
      this._audios.get(audioName).pause();
      this._audios.get(audioName).currentTime = 0;
    }
  }
  /**
   * Pause audio
   * @param audioName
   */
  pause(audioName) {
    if (this._audios.has(audioName)) {
      this._audios.get(audioName).pause();
    }
  }
}
let AudioService = /*#__PURE__*/(() => {
  class AudioService extends BaseAudioService {
    constructor() {
      super();
      /**
       * Audio src
       * @private
       */
      this.src = '/assets/audio/';
    }
    static {
      this.ɵfac = function AudioService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || AudioService)();
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: AudioService,
        factory: AudioService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return AudioService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
class BasePlatformService {
  constructor() {
    this._platformId = inject(PLATFORM_ID);
    this._browser = isPlatformBrowser(this._platformId);
  }
  get isBrowser() {
    return this._browser;
  }
}
let PlatformService = /*#__PURE__*/(() => {
  class PlatformService extends BasePlatformService {
    constructor() {
      super();
    }
    static {
      this.ɵfac = function PlatformService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || PlatformService)();
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: PlatformService,
        factory: PlatformService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return PlatformService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
const MOBILE_MAX_WIDTH = 767.98;
const SMALL_MOBILE_MAX_WIDTH = 375;
const TABLET_MAX_WIDTH = 991.98;
const SMALL_DESKTOP_MAX_WIDTH = 1199.98;
class BaseBreakpointsDetectorService {
  constructor() {
    this._platform = inject(PlatformService);
    this._windowWidth = this._platform.isBrowser ? window.innerWidth : null;
    this._isMobile = false;
    this._isSmallMobile = false;
    this._isTablet = false;
    this._isSmallDesktop = false;
    this._isDesktop = false;
    this._belowDesktop = false;
    this._belowSmallDesktop = false;
    this._belowTablet = false;
    this._aboveTablet = false;
    this.updatedBreakpoints$ = new ReplaySubject();
    this._handleResize();
    this._updateBreakpoints();
  }
  /**
   * Screen lass then MOBILE_MAX_WIDTH
   */
  get isMobile() {
    return this._isMobile;
  }
  /**
   * Screen lass then MOBILE_MAX_WIDTH
   */
  get isSmallMobile() {
    return this._isSmallMobile;
  }
  /**
   * Screen lass then TABLET_MAX_WIDTH and more then MOBILE_MAX_WIDTH
   */
  get isTablet() {
    return this._isTablet;
  }
  /**
   * Screen lass then SMALL_DESKTOP_MAX_WIDTH and more then TABLET_MAX_WIDTH
   */
  get isSmallDesktop() {
    return this._isSmallDesktop;
  }
  /**
   * Screen grater then SMALL_DESKTOP_MAX_WIDTH
   */
  get isDesktop() {
    return this._isDesktop;
  }
  /**
   * Screen lass then SMALL_DESKTOP_MAX_WIDTH
   */
  get belowDesktop() {
    return this._belowDesktop;
  }
  /**
   * Screen lass then TABLET_MAX_WIDTH
   */
  get belowSmallDesktop() {
    return this._belowSmallDesktop;
  }
  /**
   * Screen lass then MOBILE_MAX_WIDTH
   */
  get belowTablet() {
    return this._belowTablet;
  }
  /**
   * Screen more then TABLET_MAX_WIDTH
   */
  get aboveTablet() {
    return this._aboveTablet;
  }
  /**
   * Handle window resize event
   *
   * @private
   */
  _handleResize() {
    if (this._platform.isBrowser) {
      fromEvent(window, 'resize').pipe(debounceTime(100), tap(() => {
        this._windowWidth = window.innerWidth;
        this._updateBreakpoints();
      })).subscribe();
    }
  }
  /**
   * Update breakpoint values
   *
   * @private
   */
  _updateBreakpoints() {
    this._isMobile = this._windowWidth <= MOBILE_MAX_WIDTH;
    this._isSmallMobile = this._windowWidth <= SMALL_MOBILE_MAX_WIDTH;
    this._isTablet = this._windowWidth <= TABLET_MAX_WIDTH && this._windowWidth > MOBILE_MAX_WIDTH;
    this._isSmallDesktop = this._windowWidth <= SMALL_DESKTOP_MAX_WIDTH && this._windowWidth > TABLET_MAX_WIDTH;
    this._isDesktop = this._windowWidth > SMALL_DESKTOP_MAX_WIDTH;
    this._belowDesktop = this._windowWidth <= SMALL_DESKTOP_MAX_WIDTH;
    this._belowSmallDesktop = this._windowWidth <= TABLET_MAX_WIDTH;
    this._belowTablet = this._windowWidth <= MOBILE_MAX_WIDTH;
    this._aboveTablet = this._windowWidth > TABLET_MAX_WIDTH;
    this.updatedBreakpoints$.next(true);
  }
}
let BreakpointsDetectorService = /*#__PURE__*/(() => {
  class BreakpointsDetectorService extends BaseBreakpointsDetectorService {
    constructor() {
      super();
    }
    static {
      this.ɵfac = function BreakpointsDetectorService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || BreakpointsDetectorService)();
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: BreakpointsDetectorService,
        factory: BreakpointsDetectorService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return BreakpointsDetectorService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
class BaseBurgerService {
  constructor() {
    this._isOpen = false;
  }
  /**
   * Access private var
   */
  get isOpen() {
    return this._isOpen;
  }
  /**
   * Open burger
   */
  open() {
    if (!this._isOpen) {
      this._isOpen = true;
    }
  }
  /**
   * Close burger
   */
  close() {
    if (this._isOpen) {
      this._isOpen = false;
    }
  }
  /**
   * Toggle burger
   */
  toggle() {
    if (this._isOpen) {
      this.close();
    } else {
      this.open();
    }
  }
}
let BurgerService = /*#__PURE__*/(() => {
  class BurgerService extends BaseBurgerService {
    constructor() {
      super();
    }
    static {
      this.ɵfac = function BurgerService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || BurgerService)();
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: BurgerService,
        factory: BurgerService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return BurgerService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
var CreditCardType = /*#__PURE__*/function (CreditCardType) {
  CreditCardType["VISA"] = "VISA";
  CreditCardType["AMEX"] = "AMEX";
  CreditCardType["MASTERCARD"] = "MASTERCARD";
  CreditCardType["DISCOVER"] = "DISCOVER";
  CreditCardType["DINERS"] = "DINERS";
  CreditCardType["JCB"] = "JCB";
  CreditCardType["UNIONPAY"] = "UNIONPAY";
  CreditCardType["MAESTRO"] = "MAESTRO";
  CreditCardType["MIR"] = "MIR";
  return CreditCardType;
}(CreditCardType || {});
class BaseCreditCardTypeService {
  constructor() {
    this._re = {
      // starts with 34/37; 15 digits
      AMEX: /^3[47]\d{0,13}/,
      // starts with 6011/65/644-649; 16 digits
      DISCOVER: /^(?:6011|65\d{0,2}|64[4-9]\d?)\d{0,12}/,
      // starts with 300-305/309 or 36/38/39; 14 digits
      DINERS: /^3(?:0([0-5]|9)|[689]\d?)\d{0,11}/,
      // starts with 51-55/2221–2720; 16 digits
      MASTERCARD: /^(5[1-5]\d{0,2}|22[2-9]\d{0,1}|2[3-7]\d{0,2})\d{0,12}/,
      // starts with 2131/1800/35; 16 digits
      JCB: /^(?:35\d{0,2})\d{0,12}/,
      // starts with 50/56-58/6304/67; 16 digits
      MAESTRO: /^(?:5[0678]\d{0,2}|6304|67\d{0,2})\d{0,12}/,
      // starts with 22; 16 digits
      MIR: /^220[0-4]\d{0,12}/,
      // starts with 4; 16 digits
      VISA: /^4\d{0,15}/,
      // starts with 62/81; 16 digits
      UNIONPAY: /^(62|81)\d{0,14}/
    };
  }
  /**
   * Check Credir Card type by CC number
   * @param credirCardNumber
   */
  creditCardType(cc) {
    if (cc && typeof cc === 'number') {
      cc = cc.toString();
    }
    return Object.keys(this._re).find(key => this._re[key].test(cc)) || null;
  }
}
let CreditCardTypeService = /*#__PURE__*/(() => {
  class CreditCardTypeService extends BaseCreditCardTypeService {
    constructor() {
      super();
    }
    static {
      this.ɵfac = function CreditCardTypeService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || CreditCardTypeService)();
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: CreditCardTypeService,
        factory: CreditCardTypeService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return CreditCardTypeService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
class BaseFiltersService {
  constructor() {}
  /**
   * Filter array of objects by according to filter properties. Returns filtered array
   *
   * @param array
   * @param filter
   */
  filterByObject(array, filter) {
    return array.filter(item => {
      const keys = Object.keys(filter);
      if (!filter || !keys.length) {
        return true;
      }
      return keys.reduce((accumulator, key) => {
        if (!accumulator) {
          return false;
        }
        if (!isNullOrUndefined(filter[key]) && filter[key] !== '') {
          return filter[key] === item[key];
        } else {
          return true;
        }
      }, true);
    });
  }
  /**
   * Returns array of unique object values from array of objects by key
   *
   * Example:
   *  uniqueValuesByKey('type', [
   *    {type: 'deposit', amount: 1},
   *    {type: 'withdraw', amount: 2},
   *    {type: 'deposit', amount: 5},
   *  ])
   *
   * Output:
   *  ['deposit', 'withdraw']
   *
   * @param key
   */
  uniqueValuesByKey(key, array) {
    const values = [];
    array.forEach(item => {
      if (!isNullOrUndefined(item[key]) && !values.includes(item[key])) {
        values.push(item[key]);
      }
    });
    return values;
  }
  /**
   * The function `getDifference` takes in two arrays and a key, and returns the elements from the first
   * array that have a matching key in the second array.
   * @param {any[]} array1 - An array of objects that you want to compare with array2.
   * @param {any[]} array2 - array2 is an array of objects.
   * @param {any} key - The `key` parameter is the property key that is used to compare the objects in
   * `array1` and `array2`. It is the property that is used to determine if two objects are considered
   * equal.
   * @returns an array containing the objects from `array1` that have a matching `key` value with any
   * object in `array2`.
   */
  getDifference(array1, array2, key) {
    return array1.filter(object1 => {
      return array2.some(object2 => {
        return object1[key] === object2[key];
      });
    });
  }
  /**
   * Returns object of key => value pairs
   *
   * Example:
   *  keyFromValuePairs('code', 'subunits', [
   *    {code: "EUR", subunits: 100},
   *    {code: "USD", subunits: 100},
   *    {code: "BTC", subunits: 100000000},
   *  ])
   *
   * Output:
   *  {
   *    EUR: 100,
   *    USD: 100,
   *    BTC: 100000000
   *  }
   *
   * @param key
   * @param keyValue
   * @param array
   */
  keyFromValuePairs(key, keyValue, array) {
    const keyValuePairs = {};
    array.forEach(item => {
      if (!isNullOrUndefined(item[key]) && !isNullOrUndefined(item[keyValue])) {
        keyValuePairs[item[key]] = item[keyValue];
      }
    });
    return keyValuePairs;
  }
  /**
   * Returns array of objects that `key` value not in usedValues
   *
   * Examle:
   *  excludeUsedByKey('period', ['day', 'month'], [
   *    {period: 'day', value: 1},
   *    {period: 'month', value: 2},
   *    {period: 'year', value: 3},
   *  )
   *
   * Output:
   *  [
   *    {period: 'year', value: 3}
   *  ]
   *
   * @param key
   * @param usedValues
   * @param array
   */
  excludeUsedByKey(key, usedValues, array) {
    return array.filter(item => usedValues.indexOf(item[key]) === -1);
  }
  /**
   * Returns array of objects that `key` value in usedValues
   *
   * Example:
   *  excludeUsedByKey('period', ['day', 'month'], [
   *    {period: 'day', value: 1},
   *    {period: 'month', value: 2},
   *    {period: 'year', value: 3},
   *  )
   *
   * Output:
   *  [
   *    {period: 'day', value: 1},
   *    {period: 'year', value: 3},
   *  ]
   *
   * @param key
   * @param usedValues
   * @param array
   */
  includeUsedByKey(key, usedValues, array) {
    return array.filter(item => usedValues.indexOf(item[key]) !== -1);
  }
  /**
   * Returns object in which key is value of 'key' attribute and value is current item
   *
   * Example:
   *  valueAsKeyObject('slug', [
   *    {game: 'Origami', externalId: 14, slug: 'origami'},
   *    {game: 'Big Bad Wolf', externalId: 17, slug: 'big-bad-wolf'},
   *    {game: 'Vikings', externalId: 9, slug: 'vikings'},
   *  );
   *
   * Output:
   *  {
   *    origami: {game: 'Origami', externalId: 14, slug: 'origami'},
   *    big-bad-wolf: {game: 'Big Bad Wolf', externalId: 17, slug: 'big-bad-wolf'},
   *    vikings: {game: 'Vikings', externalId: 9, slug: 'vikings'}
   *  }
   *
   * @param key
   * @param array
   */
  valueAsKeyObject(key, array) {
    const result = {};
    array.map(item => {
      result[item[key]] = item;
    });
    return result;
  }
  /**
   * Returns array sorted by order Array and provided field
   *
   * Example:
   *  sortByArrayOrder([5, 4, 7], 'id', [
   *    {id: 4, name: 'Big bot crew'},
   *    {id: 5, name: 'Bird on a wire},
   *    {id: 7, name: 'Big bad wolf'},
   *  ]);
   *
   * Output:
   *  [
   *    {id: 5, name: 'Bird on a wire},
   *    {id: 4, name: 'Big bot crew'},
   *    {id: 7, name: 'Big bad wolf'},
   *  ]
   *
   * @param order
   * @param field
   * @param list
   */
  sortByArrayOrder(order, field, list) {
    const result = [];
    order.forEach(item => {
      list.forEach(listItem => {
        if (listItem[field] === item) {
          result.push(listItem);
          return;
        }
      });
    });
    return result;
  }
  /**
   * Returns array sorted by order Array and provided field.
   * If the arrays have different lengths, then we fill the array
   *
   * Example:
   *  sortByArrayOrder([5, 4, 7], 'id', [
   *    {id: 4, name: 'Big bot crew'},
   *    {id: 5, name: 'Bird on a wire},
   *    {id: 7, name: 'Big bad wolf'},
   *  ]);
   *
   * Output:
   *  [
   *    {id: 5, name: 'Bird on a wire},
   *    {id: 4, name: 'Big bot crew'},
   *    {id: 7, name: 'Big bad wolf'},
   *  ]
   *
   * @param order
   * @param field
   * @param list
   */
  sortByArrayOrderAndFillArray(order, field, list) {
    let result = [];
    order.forEach(item => {
      list.forEach(listItem => {
        if (listItem[field] === item) {
          result.push(listItem);
          return;
        }
      });
    });
    if (list?.length > order?.length) {
      result = [...result, ...list.filter(item => !order.includes(item[field]))];
    }
    return result;
  }
  /**
   * Returns array sorted by provided property
   *
   * Example:
   *  sortAlphabeticallyByProp([
   *    {name: 'Alex', age: 33},
   *    {name: 'Carl', age: 33},
   *    {name: 'Bob', age: 33}
   *  ], 'name');
   *
   * Output:
   *  [
   *    {name: 'Alex', age: 33},
   *    {name: 'Bob', age: 33},
   *    {name: 'Carl', age: 33},
   *  ]
   *
   * @param array
   * @param field
   */
  sortByProp(array, field) {
    return array.sort((a, b) => {
      if (a[field] < b[field]) {
        return -1;
      } else if (a[field] > b[field]) {
        return 1;
      } else {
        return 0;
      }
    });
  }
  /**
   * Remove empty or false values (recursive)
   *
   * Example:
   * {test: true, test1: null, test2: false} => {test: true}
   * @param obj
   * @returns
   */
  removeEmptyOrFalse(obj) {
    const self = this;
    return Object.keys(obj).filter(function (k) {
      return obj[k];
    }).reduce(function (acc, k) {
      acc[k] = typeof obj[k] === 'object' ? self.removeEmptyOrFalse(obj[k]) : obj[k];
      return acc;
    }, {});
  }
  orderBySegment(input, order, key) {
    return input.sort((a, b) => {
      const aValue = a[key],
        bValue = b[key];
      const aIndex = order.indexOf(aValue),
        bIndex = order.indexOf(bValue);
      if (aIndex === -1) return 1;else if (bIndex === -1) return -1;else return aIndex - bIndex;
    });
  }
}
let FiltersService = /*#__PURE__*/(() => {
  class FiltersService extends BaseFiltersService {
    constructor() {
      super();
    }
    static {
      this.ɵfac = function FiltersService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || FiltersService)();
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: FiltersService,
        factory: FiltersService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return FiltersService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
class BaseFullScreenService {
  constructor() {
    this._platform = inject(PlatformService);
    this._init();
  }
  /**
   * Is full screen enabled
   */
  get isFullScreen() {
    if (this._platform.isBrowser) {
      return !!(this._document.fullscreenElement || this._document.mozFullScreenElement || this._document.webkitFullscreenElement || this._document.msFullscreenElement);
    } else {
      return false;
    }
  }
  /**
   * Open element in full screen mode
   *
   * @param el
   */
  open(el = this._documentEl) {
    if (el.requestFullscreen) {
      el.requestFullscreen();
    } else if (el.mozRequestFullScreen) {
      /* Firefox */
      el.mozRequestFullScreen();
    } else if (el.webkitRequestFullscreen) {
      /* Chrome, Safari and Opera */
      el.webkitRequestFullscreen();
    } else if (el.msRequestFullscreen) {
      /* IE/Edge */
      el.msRequestFullscreen();
    }
  }
  /**
   * Close full screen mode
   */
  close() {
    if (!this.isFullScreen) {
      return;
    }
    const document = this._document;
    if (document.exitFullscreen && document.fullscreenEnabled) {
      document.exitFullscreen();
    } else if (document.mozCancelFullScreen) {
      /* Firefox */
      document.mozCancelFullScreen();
    } else if (document.webkitExitFullscreen) {
      /* Chrome, Safari and Opera */
      document.webkitExitFullscreen();
    } else if (document.msExitFullscreen) {
      /* IE/Edge */
      document.msExitFullscreen();
    }
  }
  /**
   * Toggle full screen
   */
  toggle(el = this._documentEl) {
    if (this.isFullScreen) {
      this.close();
    } else {
      this.open(el);
    }
  }
  /**
   * Emit resize event
   *
   * @private
   */
  _emitResize() {
    this._document.dispatchEvent(new Event('resize'));
  }
  /**
   * Init internal variables
   *
   * @private
   */
  _init() {
    if (this._platform.isBrowser) {
      this._document = document;
      this._documentEl = document.documentElement;
      this._handleFullScreenChangeEvent();
    }
  }
  /**
   * Emit resize on fullscreenchange event for correct change detection
   *
   * @private
   */
  _handleFullScreenChangeEvent() {
    merge(fromEvent(this._document, 'fullscreenchange'), fromEvent(this._document, 'mozfullscreenchange'), fromEvent(this._document, 'webkitfullscreenchange'), fromEvent(this._document, 'msfullscreenchange')).subscribe(e => {
      this._emitResize();
      if (this.isFullScreen) {
        this._documentEl.classList.add('full-screen');
      } else {
        this._documentEl.classList.remove('full-screen');
      }
    });
  }
}
let FullScreenService = /*#__PURE__*/(() => {
  class FullScreenService extends BaseFullScreenService {
    constructor() {
      super();
    }
    static {
      this.ɵfac = function FullScreenService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || FullScreenService)();
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: FullScreenService,
        factory: FullScreenService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return FullScreenService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
class BaseHtmlParserService {
  constructor() {
    this._accordionTitleRegex = /<h4>([^^]*?)<\/h4>/gi;
    this._accordionContentRegex = /<\/h4>([^^]*?)(<h4>|$)/gi;
    this._pageBlockTitleRegex = /<h3>([^^]*?)<\/h3>/gi;
    this._pageBlockRegex = /<\/h3>([^^]*?)(<h3>|$)/gi;
  }
  /**
   * Get accordion title list from HTML content
   *
   * @param text
   */
  getAccordionTitleList(text) {
    let match;
    const result = [];
    // tslint:disable-next-line:no-conditional-assignment
    while (match = this._accordionTitleRegex.exec(text)) {
      result.push(match[1]);
    }
    return result;
  }
  /**
   * Get accordion content list from HTML content
   *
   * @param text
   */
  getAccordionContentList(text) {
    let match;
    const result = [];
    // tslint:disable-next-line:no-conditional-assignment
    while (match = this._accordionContentRegex.exec(text)) {
      result.push(match[1]);
    }
    return result;
  }
  /**
   * Get page blocks list from HTML content
   *
   * @param text
   */
  getPageBlockList(text) {
    let match;
    const result = [];
    // tslint:disable-next-line:no-conditional-assignment
    while (match = this._pageBlockRegex.exec(text)) {
      result.push(match[1]);
    }
    return result;
  }
  /**
   * Get page blocks title list from HTML content
   *
   * @param text
   */
  getPageBlockTitleList(text) {
    let match;
    const result = [];
    // tslint:disable-next-line:no-conditional-assignment
    while (match = this._pageBlockTitleRegex.exec(text)) {
      result.push(match[1]);
    }
    return result;
  }
  replaceLineBreaks(obj) {
    for (const prop in obj) {
      if (obj[prop] !== null && typeof obj[prop] === 'string') {
        obj[prop] = obj[prop].replace(/<br\s*\/?>/gi, '');
      }
    }
  }
}
let HtmlParserService = /*#__PURE__*/(() => {
  class HtmlParserService extends BaseHtmlParserService {
    constructor() {
      super();
    }
    static {
      this.ɵfac = function HtmlParserService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || HtmlParserService)();
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: HtmlParserService,
        factory: HtmlParserService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return HtmlParserService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
class BaseLocalstorageService {
  constructor() {
    this._platform = inject(PlatformService);
    this._window = inject(WINDOW);
    this._storage = this._platform.isBrowser ? this._window?.localStorage : {};
  }
  get(key) {
    return this._storage?.getItem(key);
  }
  set(key, value) {
    this._storage?.setItem(key, value);
  }
  clearItem(key) {
    this._storage?.removeItem(key);
  }
  clearItems(keys) {
    keys?.forEach(key => this.clearItem(key));
  }
}
let LocalstorageService = /*#__PURE__*/(() => {
  class LocalstorageService extends BaseLocalstorageService {
    constructor() {
      super();
    }
    static {
      this.ɵfac = function LocalstorageService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || LocalstorageService)();
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: LocalstorageService,
        factory: LocalstorageService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return LocalstorageService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
class BasePlacesApiService {
  constructor() {
    /**
     * APP ID for api credential
     */
    this.APP_ID = '';
    /**
     * APP CODE for api credential
     */
    this.APP_CODE = '';
    this._http = inject(HttpClient);
  }
  /**
   * Get location predictions by provided params
   * @param options List of available options: https://developer.here.com/documentation/geocoder-autocomplete/topics/resource-suggest.html
   */
  getPredictions(options) {
    return this._http.get('https://autocomplete.geocoder.ls.hereapi.com/6.2/suggest.json', {
      params: {
        apiKey: this.API_KEY,
        maxresults: 20,
        ...options
      }
    }).pipe(share(), map(response => response.suggestions));
  }
  /**
   * Returns list of cities from list of all suggestions
   * @param suggestionList
   * @param maxCount
   */
  filterCities(suggestionList, maxCount = 3) {
    let count = 0;
    return !suggestionList ? [] : suggestionList.filter(suggestion => {
      if (count < maxCount && suggestion.matchLevel === 'city') {
        count++;
        return true;
      }
      return false;
    });
  }
  /**
   * Returns list of streets from list of all suggestions
   * @param suggestionList
   * @param city
   * @param maxCount
   */
  filterAddress(suggestionList, city = '', maxCount = 3) {
    let count = 0;
    return !suggestionList ? [] : suggestionList.filter(suggestion => {
      if (count < maxCount && (suggestion.matchLevel === 'houseNumber' || suggestion.matchLevel === 'street')) {
        if (city) {
          if (suggestion.address.city === city) {
            count++;
            return true;
          }
        } else {
          count++;
          return true;
        }
      }
      return false;
    });
  }
}
/**
 * ************************ LIBRARY DOCUMENTATION *****************************
 *
 * https://developer.here.com/documentation/geocoder-autocomplete/topics/quick-start-get-suggestions.html
 *
 * ****************************************************************************
 */
let PlacesApiService = /*#__PURE__*/(() => {
  class PlacesApiService extends BasePlacesApiService {
    constructor() {
      super();
      /**
       * Api Key - uses for new version of API
       */
      this.API_KEY = '35l6yXlnh6RwmTjjHyCujdAiue1wDPCFAVxl_b7bwbQ';
    }
    static {
      this.ɵfac = function PlacesApiService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || PlacesApiService)();
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: PlacesApiService,
        factory: PlacesApiService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return PlacesApiService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
class BaseProcessLinkService {
  constructor() {}
  /**
   * Return href from click event
   * @param e
   */
  getHrefFromClickEvent(e) {
    const element = e?.target;
    return element.nodeName === 'A' ? element.getAttribute('href') : null;
  }
  /**
   * Get id attribute from click event
   * @param e
   */
  getIdAttributeFromClickEvent(e) {
    const element = e?.target;
    return element?.id;
  }
}
let ProcessLinkService = /*#__PURE__*/(() => {
  class ProcessLinkService extends BaseProcessLinkService {
    constructor() {
      super();
    }
    static {
      this.ɵfac = function ProcessLinkService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || ProcessLinkService)();
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: ProcessLinkService,
        factory: ProcessLinkService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return ProcessLinkService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
class BaseScriptLoaderService {
  constructor() {
    /**
     * Storage for script loaders observables by url
     */
    this._scriptLoaders = new Map();
    this._platform = inject(PlatformService);
  }
  /**
   * Load script by url and append to provided HTML element or head if element not provided
   *
   * @param url
   * @param target
   * @param attributes
   */
  load(url, target = 'head', attributes) {
    if (!this._platform.isBrowser) {
      return;
    }
    if (this._scriptLoaders.has(url)) {
      return this._scriptLoaders.get(url);
    }
    const loader = new Observable(observer => {
      const script = document.createElement('script');
      if (attributes) {
        Object.keys(attributes).forEach(key => script.setAttribute(key, attributes[key]));
      }
      script.onload = e => {
        observer.next(e);
        observer.complete();
      };
      script.onerror = error => {
        observer.error(error);
      };
      script.src = url;
      const targetEl = typeof target === 'string' ? document.querySelector(target) : target;
      targetEl?.appendChild(script);
    }).pipe(shareReplay(1));
    this._scriptLoaders.set(url, loader);
    return loader;
  }
}
let ScriptLoaderService = /*#__PURE__*/(() => {
  class ScriptLoaderService extends BaseScriptLoaderService {
    constructor() {
      super();
    }
    static {
      this.ɵfac = function ScriptLoaderService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || ScriptLoaderService)();
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: ScriptLoaderService,
        factory: ScriptLoaderService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return ScriptLoaderService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * Available tournament statuses
 */
var TimelineStatus = /*#__PURE__*/function (TimelineStatus) {
  TimelineStatus["FUTURE"] = "future";
  TimelineStatus["NOW"] = "now";
  TimelineStatus["PAST"] = "past";
  return TimelineStatus;
}(TimelineStatus || {});
class BaseTimeService {
  constructor() {
    this._platform = inject(PlatformService);
  }
  /**
   * Convert provided date string to local date object
   *
   * @param date
   */
  toLocalDate(date) {
    if (date) {
      const dateNumbers = date.split(/\D/);
      return new Date(Date.UTC(parseInt(dateNumbers[0], 10) || 0, parseInt(dateNumbers[1], 10) - 1 || 0, parseInt(dateNumbers[2], 10) || 0, parseInt(dateNumbers[3], 10) || 0, parseInt(dateNumbers[4], 10) || 0, parseInt(dateNumbers[5], 10) || 0, parseInt(dateNumbers[6], 10) || 0));
    } else {
      return new Date();
    }
  }
  parseDate(date) {
    const values = date.split(/[^0-9]/);
    const year = parseInt(values[0], 10);
    const month = parseInt(values[1], 10) - 1;
    const day = parseInt(values[2], 10);
    const hours = parseInt(values[3], 10);
    const minutes = parseInt(values[4], 10);
    const seconds = parseInt(values[5], 10);
    return new Date(year, month, day, hours, minutes, seconds);
  }
  /**
   * Returns diff object between current and provided time
   *
   * @param targetTime
   */
  timeDiff(targetTime) {
    const currentDate = new Date();
    const dateDiff = Math.abs(targetTime.getTime() - currentDate.getTime());
    const days = Math.floor(dateDiff / (1000 * 3600 * 24));
    const hours = Math.floor(dateDiff / (1000 * 3600) - days * 24);
    const minutes = Math.floor(dateDiff % (1000 * 3600 * 24) % (1000 * 3600) / (1000 * 60));
    const seconds = Math.floor(dateDiff % 60000 / 1000);
    return {
      days,
      hours,
      minutes,
      seconds,
      daysLabel: this.pluralizeDateLabel(days, 'labl.day'),
      hoursLabel: this.pluralizeDateLabel(hours, 'labl.hr'),
      minLabel: this.pluralizeDateLabel(minutes, 'labl.min'),
      secLabel: this.pluralizeDateLabel(minutes, 'labl.sec')
    };
  }
  /**
   * Pluralise label string depend on provided number
   *
   * @param num
   * @param label
   */
  pluralizeDateLabel(num, label) {
    return num > 1 ? label + 's' : label;
  }
  currentDate$() {
    if (this._platform.isBrowser) {
      const interval$ = timer(0, 1000);
      return interval$.pipe(map(() => {
        const now = new Date();
        const min = (now.getMinutes() < 10 ? '0' : '') + now.getMinutes(); // minutes
        const h = (now.getHours() < 10 ? '0' : '') + now.getHours(); // hours
        return {
          min,
          h
        };
      }));
    } else {
      return of({
        min: '',
        h: ''
      });
    }
  }
  /**
   * Returns timeline status from provided dates
   *
   * @param start
   * @param end
   * @private
   */
  resolveTimeStatus(start, end) {
    const currentDate = new Date();
    if (currentDate < start) {
      return TimelineStatus.FUTURE;
    } else if (currentDate > end) {
      return TimelineStatus.PAST;
    } else {
      return TimelineStatus.NOW;
    }
  }
  detectEndOfDay() {
    const end = new Date();
    end.setHours(23, 59, 59, 999);
    return end.toISOString();
  }
  /**
   * Check if is it today date
   *
   * @param date
   */
  isTodayDate(date) {
    const currentDate = new Date();
    const currentDataUTC = new Date(currentDate.getTime() + currentDate.getTimezoneOffset() * 60000);
    const dateToCompareUTC = new Date(date.getTime() + date.getTimezoneOffset() * 60000);
    return currentDataUTC.toDateString() === dateToCompareUTC.toDateString();
  }
  /**
   * Convert date to timestamp
   * @param strDate
   * @private
   */
  dateToTimestamp(strDate) {
    return Date.parse(strDate) / 1000;
  }
  /**
   * Check UTC bonus format dates
   * @param createdAt
   * @private
   */
  areSameUTCDates(createdAt) {
    return new Date().getUTCDay() === new Date(createdAt).getUTCDay() && new Date().getUTCMonth() === new Date(createdAt).getUTCMonth() && new Date().getUTCFullYear() === new Date(createdAt).getUTCFullYear();
  }
}
let TimeService = /*#__PURE__*/(() => {
  class TimeService extends BaseTimeService {
    constructor() {
      super();
    }
    static {
      this.ɵfac = function TimeService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || TimeService)();
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: TimeService,
        factory: TimeService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return TimeService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
class BaseVisibilityService {
  constructor() {
    this._document = inject(DOCUMENT);
    this._pageVisible$ = concat(defer(() => of(!this._document.hidden)), fromEvent(this._document, 'visibilitychange').pipe(map(e => !this._document.hidden)));
  }
  get pageIsVisible$() {
    return this._pageVisible$;
  }
}
let VisibilityService = /*#__PURE__*/(() => {
  class VisibilityService extends BaseVisibilityService {
    constructor() {
      super();
    }
    static {
      this.ɵfac = function VisibilityService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || VisibilityService)();
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: VisibilityService,
        factory: VisibilityService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return VisibilityService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
class BaseCacheService {
  constructor() {
    /**
     * Default expiration time for cached items
     */
    this.DEF_EXPIRATION_TIME = 3600000;
    /**
     * Cache Map
     */
    this._cache = new Map();
  }
  /**
   * Delete value from cache
   *
   * @param key
   */
  delete(key) {
    if (this.has(key)) {
      this._cache.delete(key);
    }
  }
  /**
   * Delete all items from cache
   */
  clear() {
    this._cache = new Map();
  }
  /**
   * Get value from cache
   *
   * @param key
   */
  get(key) {
    if (this.has(key)) {
      return of(this._cache.get(key)?.content);
    } else {
      throw new Error('Provided key dos not exists in cache');
    }
  }
  /**
   * Is cache exists value by provided key
   *
   * @param key
   */
  has(key) {
    // @ts-ignore
    return this._cache.has(key) && this._cache.get(key).expires > Date.now();
  }
  /**
   * Add content in cache
   *
   * @param key
   * @param content
   * @param age
   */
  set(key, content, age) {
    this._cache.set(key, {
      expires: Date.now() + (age || this.DEF_EXPIRATION_TIME),
      content
    });
  }
}
let CacheService = /*#__PURE__*/(() => {
  class CacheService extends BaseCacheService {
    constructor() {
      super();
    }
    static {
      this.ɵfac = function CacheService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || CacheService)();
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: CacheService,
        factory: CacheService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return CacheService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
let ConfettiService = /*#__PURE__*/(() => {
  class ConfettiService {
    constructor() {
      this.confetti$ = inject(CONFETTI$);
      /**
       * Is ready to use source
       */
      this._ready$ = new ReplaySubject(1);
      this.confetti$.subscribe(() => {
        this._ready$.next(true);
      });
    }
    createConfetti(callback, options = {
      particleCount: 100,
      spread: 60
    }, timeOut = 300) {
      this._ready$.pipe(first()).subscribe(() => {
        confetti({
          ...options
        });
      });
      setTimeout(() => {
        return callback();
      }, timeOut);
    }
    static {
      this.ɵfac = function ConfettiService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || ConfettiService)();
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: ConfettiService,
        factory: ConfettiService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return ConfettiService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
const TITLE_TAGS = ['H1', 'H2', 'H3', 'H4', 'H5', 'H6'];
class BaseFaqSchemaService {
  constructor() {
    /**
     * Chunks elements for _contentElements
     * @private
     */
    this._chunksElements = [];
    /**
     * Title indexes for create _chunksElements
     * @private
     */
    this._titleIndexes = [];
  }
  /**
   * Parse content and add fqg schema
   * @param content
   */
  addFaqSchema(content) {
    this._renewInstances();
    this._parser = new DOMParser();
    const contentHTML = this._parser.parseFromString(content, 'text/html').body;
    this._contentElements = Array.from(contentHTML.childNodes);
    this._resultHTML = document.createElement('div');
    this._pushTitleIndexes();
    this._createChuncksElements();
    this._createParentDivsForAnswerText();
    this._createParentAndAddAttrs();
    return this._resultHTML.innerHTML;
  }
  /**
   * Push indexes title for create chunks
   * @private
   */
  _pushTitleIndexes() {
    this._contentElements?.forEach((el, i) => {
      if (el && el.tagName && TITLE_TAGS.includes(el.tagName)) {
        el.setAttribute('itemprop', 'name');
        this._titleIndexes.push(i);
      }
    });
  }
  /**
   * Create chunks from _titleIndexes
   * @private
   */
  _createChuncksElements() {
    if (this._titleIndexes.length > 0) {
      this._titleIndexes.forEach((num, i, arr) => {
        if (i === arr.length - 1) {
          this._chunksElements.push(this._contentElements?.slice(num));
        } else {
          this._chunksElements.push(this._contentElements?.slice(num, arr[i + 1]));
        }
      });
    }
  }
  _createParentDivsForAnswerText() {
    this._chunksElements = this._chunksElements.map(chunk => {
      if (Array.isArray(chunk)) {
        return [chunk[0], this._createTextSchema(chunk.slice(1))];
      }
    });
  }
  /**
   * Create parent element and add element from chunk
   * @private
   */
  _createParentAndAddAttrs() {
    if (this._chunksElements.length > 0) {
      this._chunksElements.forEach(chunk => {
        const parentDiv = this._createParentSchema();
        if (Array.isArray(chunk)) {
          parentDiv.append(...chunk);
        }
        this._resultHTML?.append(parentDiv);
      });
    }
  }
  /**
   * Create parent element and add schema attributes
   * @private
   */
  _createParentSchema() {
    const div = document.createElement('div');
    div.setAttribute('itemscope', '');
    div.setAttribute('itemprop', 'mainEntity');
    div.setAttribute('itemtype', 'https://schema.org/Question');
    return div;
  }
  /**
   * Create text schema element and add schema attributes
   * @private
   */
  _createTextSchema(partChunks) {
    const parentAnswer = document.createElement('div');
    parentAnswer.setAttribute('itemscope', '');
    parentAnswer.setAttribute('itemprop', 'acceptedAnswer');
    parentAnswer.setAttribute('itemtype', 'https://schema.org/Answer');
    const textAnswer = document.createElement('div');
    textAnswer.setAttribute('itemprop', 'text');
    textAnswer.append(...partChunks);
    parentAnswer.appendChild(textAnswer);
    return parentAnswer;
  }
  /**
   * Renew all instances before use
   * @private
   */
  _renewInstances() {
    this._resultHTML = null;
    this._contentElements = null;
    this._parser = null;
    this._chunksElements = [];
    this._titleIndexes = [];
  }
}
let FaqSchemaService = /*#__PURE__*/(() => {
  class FaqSchemaService extends BaseFaqSchemaService {
    constructor() {
      super();
    }
    static {
      this.ɵfac = function FaqSchemaService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || FaqSchemaService)();
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: FaqSchemaService,
        factory: FaqSchemaService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return FaqSchemaService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * Error messages by keys
 */
const ERROR_MESSAGES = {
  required: 't.cant-blank',
  required_true: 't.must-accepted',
  pattern: 't.invalid-format',
  password_pattern: 't.paswd-requirments',
  password_length: 't.length-symbols',
  must_match: 't.passwds-not-match',
  eighteen_years: 't.18-years-err',
  min: 't.amount-below-limit',
  max: 't.amount-above-limit',
  maxlength: 't.invalid-format',
  minlength: 't.invalid-format',
  ca_postal_code_error: 't.postal-code-error-for-ca',
  au_postal_code_error: 't.postal-code-error-for-au'
};
class BaseFormsErrorHandlerService {
  constructor() {}
  /**
   * Apply form errors with messages
   * And return object with errors for each key
   *
   * @param form
   * @param ssResponse
   * @param touch
   */
  applyFormErrors(form, ssResponse = null, touch = false) {
    const formErrors = {};
    ssResponse = ssResponse || {
      errors: {}
    };
    Object.keys(form.controls).forEach(controlKey => {
      const control = form.get(controlKey);
      let errors = {
        ...(control.errors || {}),
        ...(ssResponse.errors && ssResponse.errors[controlKey] || {})
      };
      const errorKeys = Object.keys(errors);
      if (errorKeys.length) {
        errorKeys.forEach(errorKey => {
          errors[errorKey] = this.getErrorMessageByKey(errorKey) || errors[errorKey];
        });
      } else {
        errors = null;
      }
      control.setErrors(errors);
      formErrors[controlKey] = this.errors(control);
      if (touch) {
        control.markAsTouched();
      }
    });
    /**
     * Uses for get errors which key not match control key
     */
    Object.keys(ssResponse).forEach(errorKey => {
      Object.keys(ssResponse[errorKey]).forEach(key => {
        if (!formErrors[key]) {
          formErrors[key] = Object.keys(ssResponse[errorKey][key]).map(error => ssResponse[errorKey][key][error]);
        }
      });
    });
    return formErrors;
  }
  /**
   * Returns array of error messages if exists for provided control
   *
   * @param control
   */
  errors(control) {
    return Object.keys(control.errors || {}).map(key => {
      if (key === 't.min' || key === 't.max') {
        return `${key} ${control.errors[key][key]}`;
      } else {
        return this.getErrorMessageByKey(key) || control.errors[key];
      }
    });
  }
  /**
   * Returns error message for provided key
   *
   * @param key
   */
  getErrorMessageByKey(key) {
    return ERROR_MESSAGES[key] || '';
  }
  /**
   * Convert SS error response to array of errors
   *
   * @param error
   * @param includKeyInMessage
   */
  ssBackendErrorsToArray(error, includKeyInMessage) {
    const errorList = [];
    if (error) {
      Object.entries(error).forEach(([key, value]) => {
        const type = typeof error[key];
        if (!Boolean(this._mainKeyForMessage) && key !== 'errors') {
          this._mainKeyForMessage = key;
        }
        if (type === 'object') {
          errorList.push(...this.ssBackendErrorsToArray(error[key], includKeyInMessage));
        } else if (type === 'string') {
          errorList.push(includKeyInMessage ? `${this._mainKeyForMessage[0]?.toUpperCase() + this._mainKeyForMessage.slice(1)}: ${value}` : error[key]);
        }
      });
    }
    return errorList;
  }
}
let FormsErrorHandlerService = /*#__PURE__*/(() => {
  class FormsErrorHandlerService extends BaseFormsErrorHandlerService {
    constructor() {
      super();
    }
    static {
      this.ɵfac = function FormsErrorHandlerService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || FormsErrorHandlerService)();
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: FormsErrorHandlerService,
        factory: FormsErrorHandlerService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return FormsErrorHandlerService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
let WindowService = /*#__PURE__*/(() => {
  class WindowService {
    constructor() {
      this._window = inject(WINDOW);
      this._isIphoneDevices = this._isDetectOs(/ipad|iphone|ipod/i);
      this._isSafari = this._isDetectOs(/^((?!chrome|android).)*safari/i);
    }
    get nativeWindow() {
      return this._window;
    }
    get isIphoneDevices() {
      return this._isIphoneDevices;
    }
    get isSafari() {
      return this._isSafari;
    }
    /**
     * Return true if application runs on specific OS
     *
     * @private
     */
    _isDetectOs(regexp) {
      return regexp.test(this._window.navigator.userAgent) || !!this._window.navigator.platform && regexp.test(this._window.navigator.platform);
    }
    get isIpadOsDevices() {
      return this._window.navigator.maxTouchPoints > 2 && /MacIntel/.test(this._window?.navigator?.platform);
    }
    static {
      this.ɵfac = function WindowService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || WindowService)();
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: WindowService,
        factory: WindowService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return WindowService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * Generated bundle index. Do not edit.
 */

export { ArrayService, AudioService, BaseArrayService, BaseAudioService, BaseBreakpointsDetectorService, BaseBurgerService, BaseCacheService, BaseCreditCardTypeService, BaseFaqSchemaService, BaseFiltersService, BaseFormsErrorHandlerService, BaseFullScreenService, BaseHtmlParserService, BaseLocalstorageService, BasePlacesApiService, BasePlatformService, BaseProcessLinkService, BaseScriptLoaderService, BaseTimeService, BaseVisibilityService, BreakpointsDetectorService, BurgerService, CacheService, ConfettiService, CreditCardType, CreditCardTypeService, ERROR_MESSAGES, FaqSchemaService, FiltersService, FormsErrorHandlerService, FullScreenService, HtmlParserService, LocalstorageService, PlacesApiService, PlatformService, ProcessLinkService, ScriptLoaderService, TimeService, TimelineStatus, VisibilityService, WindowService, array_service as utils };
